<script lang="ts">
    import { theme } from '../stores/theme';
    import { locale, t, SUPPORTED_LOCALES, type Locale } from '../stores/locale';
    import ToggleInput from '../components/forms/ToggleInput.svelte';
    import Select from '../components/forms/Select.svelte';
    import Icon from '../components/Icon.svelte';

    let errors: Record<string, string[]> = {};

    const setLocale = (e: CustomEvent<string>) => locale.set(e.detail as Locale);
</script>

<div class="page">
    <h1>{$t('settings.title')}</h1>

    <section class="card settings-card">
        <h2>{$t('settings.appearance')}</h2>

        <div class="setting-list">
            <div class="setting-row">
                <div class="setting-icon">
                    <Icon name="moon" size={20} />
                </div>
                <div class="setting-info">
                    <span class="setting-label">{$t('settings.darkMode')}</span>
                    <span class="setting-desc">{$t('settings.darkModeDesc')}</span>
                </div>
                <div class="setting-action">
                    <ToggleInput
                        {errors}
                        name="dark-mode"
                        checked={$theme === 'dark'}
                        on:change={theme.toggle}
                    />
                </div>
            </div>

            <div class="setting-row">
                <div class="setting-icon">
                    <Icon name="globe" size={20} />
                </div>
                <div class="setting-info">
                    <span class="setting-label">{$t('settings.language')}</span>
                    <span class="setting-desc">{$t('settings.languageDesc')}</span>
                </div>
                <div class="setting-action select-wrap">
                    <Select
                        {errors}
                        name="language"
                        options={SUPPORTED_LOCALES.map(l => ({ value: l.code, label: `${l.name} (${l.code.toUpperCase()})` }))}
                        value={$locale}
                        on:change={setLocale}
                    />
                </div>
            </div>
        </div>
    </section>
</div>

<style lang="scss">
    .settings-card {
        padding: 0 !important; // Controlled by setting-row
    }

    .setting-list {
        padding: 0.5em 0;
    }

    .setting-row {
        display: flex;
        align-items: center;
        gap: 1.5em;
        padding: 1.25em 1.5em;
        transition: background-color 0.2s ease;
        position: relative;
        cursor: default;

        &:hover {
            background-color: $color-bg-offset;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 1.5em;
            right: 1.5em;
            height: 1px;
            background-color: $color-border;
            opacity: 0.4;
        }

        &:last-child::after {
            display: none;
        }
    }

    .setting-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: rgba($color-primary, 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .setting-info {
        display: flex;
        flex-direction: column;
        gap: 0.2em;
        flex: 1;
        min-width: 0;
    }

    .setting-label {
        font-size: 1em;
        font-weight: 500;
        color: $color-text;
    }

    .setting-desc {
        font-size: 0.85em;
        color: $color-text-secondary;
    }

    .setting-action {
        flex-shrink: 0;

        &.select-wrap {
            min-width: 160px;
        }

        // Standardize form controls in rows
        :global(.form-field) {
            margin-bottom: 0;
            gap: 0;
        }

    }
</style>

